import { Page, PageRevisionResult } from 'referral-types';
import { pageApi } from '../../api/service';
import type { FormFields } from './page.schema';

// UPDATE PAGE THEME

export const updatePageMutation = async (
  pageId: string,
  updates: FormFields,
  pageRevisions: PageRevisionResult,
  files?: {
    logoFile?: File | null;
    backgroundFile?: File | null;
    backgroundMobileFile?: File | null;
    groupMediaFile?: File | null;
    headerMediaFile?: File | null;
  }
): Promise<PageRevisionResult> => {
  const updatedDraftRevision = await pageApi.updatePage(pageId, updates, files);

  return {
    revision: pageRevisions.revision,
    draftRevision: updatedDraftRevision,
  };
};

export const updatePageOptions = (updates: FormFields, pageRevisions: PageRevisionResult) => ({
  optimisticData: getOptimisticPageRevision(updates, pageRevisions),
  rollbackOnError: true,
  populateCache: true,
  revalidate: false,
});

const getOptimisticPageRevision = (updates: FormFields, pageRevisions: PageRevisionResult): PageRevisionResult => {
  const hasBackgroundThemeChanges = Object.keys(updates.backgroundThemeRevision ?? {}).length > 0;
  const hasPageThemeChanges = Object.keys(updates.pageThemeRevision ?? {}).length > 0;
  const hasGroupThemeChanges = Object.keys(updates.groupThemeRevision ?? {}).length > 0;
  const hasReferralThemeChanges = Object.keys(updates.referralThemeRevision ?? {}).length > 0;
  const hasNavbarThemeChanges = Object.keys(updates.navbarThemeRevision ?? {}).length > 0;
  const hasHeaderThemeChanges = Object.keys(updates.headerThemeRevision ?? {}).length > 0;

  return {
    revision: pageRevisions.revision,
    draftRevision: {
      ...pageRevisions.draftRevision!,
      backgroundThemeRevisionId: hasBackgroundThemeChanges
        ? `${pageRevisions.draftRevision!.backgroundThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.backgroundThemeRevisionId,
      backgroundThemeRevision: {
        ...pageRevisions.draftRevision!.backgroundThemeRevision,
        ...updates.backgroundThemeRevision,
      },

      pageThemeRevisionId: hasPageThemeChanges
        ? `${pageRevisions.draftRevision!.pageThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.pageThemeRevisionId,
      pageThemeRevision: { ...pageRevisions.draftRevision!.pageThemeRevision, ...updates.pageThemeRevision },

      groupThemeRevisionId: hasGroupThemeChanges
        ? `${pageRevisions.draftRevision!.groupThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.groupThemeRevisionId,
      groupThemeRevision: { ...pageRevisions.draftRevision!.groupThemeRevision, ...updates.groupThemeRevision },

      referralThemeRevisionId: hasReferralThemeChanges
        ? `${pageRevisions.draftRevision!.referralThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.referralThemeRevisionId,
      referralThemeRevision: {
        ...pageRevisions.draftRevision!.referralThemeRevision,
        ...updates.referralThemeRevision,
      },

      navbarThemeRevisionId: hasNavbarThemeChanges
        ? `${pageRevisions.draftRevision!.navbarThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.navbarThemeRevisionId,
      navbarThemeRevision: { ...pageRevisions.draftRevision!.navbarThemeRevision, ...updates.navbarThemeRevision },

      headerThemeRevisionId: hasHeaderThemeChanges
        ? `${pageRevisions.draftRevision!.headerThemeRevisionId}-invalid`
        : pageRevisions.draftRevision!.headerThemeRevisionId,
      headerThemeRevision: { ...pageRevisions.draftRevision!.headerThemeRevision, ...updates.headerThemeRevision },
    },
  };
};

// PUBLISH PAGE THEME

export const publishPageMutation = async (pageId: string): Promise<Page> => {
  return await pageApi.publishPage(pageId);
};

export const publishPageOptions = (page: Page) => ({
  optimisticData: { ...page, revisionId: page.draftRevisionId },
  rollbackOnError: true,
  populateCache: true,
  revalidate: false,
});
